import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../layout/buttons';
import styles from './SignatureChat.module.css';
import { BoopAnimate, BoopTalk } from '../animations/boopAnimate';
import boopStatic from '../../assets/icons/augustsays/Boop-colour2.svg';
import { fetchCanvassStatus } from '../../utils/canvasses';
import { fetchInterviewBySessionId } from '../../utils/interviews';
import { Container } from '../layout';
import { validateInterview, submitPreambleAnswers } from '../../utils/interviews';
import { logger } from '../../utils/logger';
import ReactMarkdown from 'react-markdown';
import { config } from '../../config/environment';

const SignatureChat = ({
  runtimeUrl,
  interviewId,
  onPills,
  onIntroduction,
  onInterviewId,
  onShouldTerminate,
  autoStart = true,
  disabled = false,
  disabledPlaceholder = 'Thank you so much for your time and insights. (Signature)',
  introductionData,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [threadId, setThreadId] = useState('');
  const listRef = useRef(null);
  const portraitRef = useRef(null);
  const [canStart, setCanStart] = useState(null);
  const [validationModal, setValidationModal] = useState({ open: false, summary: '', sessionId: null });
  const [isValidating, setIsValidating] = useState(false);
  const [isInitialStart, setIsInitialStart] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [speakingIndex, setSpeakingIndex] = useState(null);
  const [showEndInterviewModal, setShowEndInterviewModal] = useState(false);
  const [shouldShowMobileEndButton, setShouldShowMobileEndButton] = useState(true);
  const [preambleQuestions, setPreambleQuestions] = useState([]);
  const [currentPreambleIndex, setCurrentPreambleIndex] = useState(-1);
  const [preambleAnswers, setPreambleAnswers] = useState({});
  const [showPreambleQuestions, setShowPreambleQuestions] = useState(false);
  const [showContinueConfirmation, setShowContinueConfirmation] = useState(false);
  const [preambleCompleted, setPreambleCompleted] = useState(false);
  const [showContinuePrompt, setShowContinuePrompt] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [canvassData, setCanvassData] = useState(null);
  const [canvassName, setCanvassName] = useState('');
  const [canvassDescription, setCanvassDescription] = useState('');
  const [preemptiveRequest, setPreemptiveRequest] = useState(null);
  const [isPreemptiveLoading, setIsPreemptiveLoading] = useState(false);
  
  // signature state
  const [signatureState, setSignatureState] = useState({
    emotional_tone: 'neutral',
    engagement_level: 'low',
    cluster_progress: 'Initial',
    signature_element: 'circle_soft',
    visual_intensity: 0.3,
    emotions: [],
    processed_emotions: [],
    beads: 3
  });
  
  const [portraitHeight, setPortraitHeight] = useState(250);
  const [showSignature, setShowSignature] = useState(false);
  const navigate = useNavigate();

  // emotion palette
  const emotionPalette = {
    joy: { color: '#FFC7B4', label: 'joy' },
    pride: { color: '#B57BC0', label: 'pride' },
    belonging: { color: '#7FB0D9', label: 'belonging' },
    hope: { color: '#6FD3A6', label: 'hope' },
    nostalgia: { color: '#E8A98C', label: 'nostalgia' },
    frustration: { color: '#D19B6B', label: 'frustration' },
    nerves: { color: '#93A6B8', label: 'nerves' },
    habit: { color: '#B9B9B2', label: 'habit' }
  };

  // Map API emotions to palette emotions
  const emotionMapping = {
    // Positive emotions
    'joy': 'joy',
    'amusement': 'joy',
    'sensory_pleasure': 'joy',
    'serenity': 'joy',
    'relief': 'joy',
    'satisfaction': 'joy',
    'euphoria': 'joy',
    'happy_for': 'joy',
    'hope': 'hope',
    'inspiration': 'hope',
    'awe': 'hope',
    'excitement': 'hope',
    'pride': 'pride',
    'admiration': 'pride',
    'elevation': 'pride',
    'gratitude': 'pride',
    'recognition': 'pride',
    'determination': 'pride',
    'fascination': 'pride',
    'positive_surprise': 'pride',
    
    // Social emotions
    'belonging': 'belonging',
    'psychological_safety': 'belonging',
    'affection': 'belonging',
    'tenderness': 'belonging',
    'lust': 'belonging',
    
    // Negative emotions
    'frustration': 'frustration',
    'anger': 'frustration',
    'indignation': 'frustration',
    'resentment': 'frustration',
    'annoyance': 'frustration',
    'dissatisfaction': 'frustration',
    'contempt': 'frustration',
    'hate': 'frustration',
    'disgust': 'frustration',
    
    // Anxiety/nervous emotions
    'nerves': 'nerves',
    'fear': 'nerves',
    'startle': 'nerves',
    'worry': 'nerves',
    'anxiety': 'nerves',
    'distrust': 'nerves',
    'doubt': 'nerves',
    'nervousness': 'nerves',
    'insecurity': 'nerves',
    'distress': 'nerves',
    'desperation': 'nerves',
    'confusion': 'nerves',
    'shock': 'nerves',
    
    // Sadness/neutral emotions
    'sadness': 'habit',
    'disappointment': 'habit',
    'pity': 'habit',
    'loneliness': 'habit',
    'rejection': 'habit',
    'humiliation': 'habit',
    'longing': 'habit',
    'envy': 'habit',
    'jealousy': 'habit',
    'guilt': 'habit',
    'regret': 'habit',
    'shame': 'habit',
    'embarrassment': 'habit',
    'boredom': 'habit',
    'reluctance': 'habit',
    'nostalgia': 'nostalgia',
    
    // Legacy mappings
    'trust': 'belonging',
    'safe': 'belonging',
    'safety': 'belonging',
    'autonomy': 'pride',
    'control': 'pride',
    'burnout': 'frustration',
    'exhausted': 'frustration'
  };

  useEffect(() => {
    try {
      const dismissed = window?.localStorage?.getItem('augustsays_welcome_dismissed') === '1';
      setShowWelcomeModal(false);
    } catch {
      setShowWelcomeModal(false);
    }
  }, []);

  // Listen for end interview request from navigation bar
  useEffect(() => {
    const handleEndInterviewRequested = () => {
      setShowEndInterviewModal(true);
    };

    window.addEventListener('endInterviewRequested', handleEndInterviewRequested);
    
    return () => {
      window.removeEventListener('endInterviewRequested', handleEndInterviewRequested);
    };
  }, []);

  const dismissWelcomeModal = () => {
    try {
      window?.localStorage?.setItem('augustsays_welcome_dismissed', '1');
    } catch {}
    setShowWelcomeModal(false);
  };

  useEffect(() => {
    let aborted = false;
    const run = async () => {
      try {
        if (!interviewId) { setCanStart(false); return; }
        const status = await fetchCanvassStatus({ canvassId: interviewId });
        const statusValue = (status?.status || '').toLowerCase();
        const introductionText = status?.introduction_text || null;

        if (introductionText) {
          const updatedIntroductionData = {
            ...introductionData,
            introduction_text: introductionText
          };
          onIntroduction(updatedIntroductionData);
        }

        if(status.name) {
          setCanvassName(status.name);
        }
        
        if(status.description) {
          setCanvassDescription(status.description);
        }
        
        if (!aborted && status) {
          setCanvassData(status);
          const preambleQs = status.preamble_questions || [];
          setPreambleQuestions(preambleQs);
          
          console.log('[Preamble Setup] Preamble questions:', preambleQs);
          console.log('[Preamble Setup] Current state:', {
            currentPreambleIndex, preambleCompleted, preambleQsLength: preambleQs.length
          });
          
          if (preambleQs.length > 0 && currentPreambleIndex === -1 && !preambleCompleted) {
            console.log('[Preamble Setup] Showing preamble questions');
            setShowPreambleQuestions(true);
            setCurrentPreambleIndex(0);
            setCanStart(false);
          } else {
            console.log('[Preamble Setup] Not showing preamble questions');
            setCanStart(statusValue === 'approved' || statusValue === 'active');
          }
        }
      } catch {
        if (!aborted) setCanStart(false);
      }
    };
    run();
    return () => { aborted = true; };
  }, [interviewId, preambleCompleted]);

  // Debug: Track showPreambleQuestions changes
  useEffect(() => {
    console.log('[State Debug] showPreambleQuestions changed:', showPreambleQuestions);
    console.log('[State Debug] Current state:', {
      showPreambleQuestions,
      showContinueConfirmation,
      preambleAnswersCount: Object.keys(preambleAnswers).length,
      preambleCompleted,
      currentPreambleIndex
    });
  }, [showPreambleQuestions]);

  // Handle preamble completion - show continue prompt when ready
  useEffect(() => {
    const shouldShowContinuePrompt = !showPreambleQuestions && 
                                   !showContinueConfirmation && 
                                   Object.keys(preambleAnswers).length > 0 && 
                                   !preambleCompleted;
    
    setShowContinuePrompt(shouldShowContinuePrompt);
  }, [showPreambleQuestions, showContinueConfirmation, preambleAnswers, preambleCompleted]);

  
  const handleValidationResponse = async (isUserValidated) => {
    if (!validationModal.sessionId) {
      setValidationModal({ open: false, summary: '', sessionId: null });
      return;
    }
    if (isValidating) return;
    setIsValidating(true);
    try {
      await validateInterview({
        sessionId: validationModal.sessionId,
        isUserValidated,
      });
    } catch (err) {
      logger.error('Failed to validate interview summary:', err);
    } finally {
      setIsValidating(false);
      setValidationModal({ open: false, summary: '', sessionId: null });
      if (isUserValidated) {
        navigate('/voice/complete');
      } else {
        const interviewUrl = window.location.href;
        navigate(`/voice/completed?interviewUrl=${encodeURIComponent(interviewUrl)}`);
      }
    }
  };

  const voiceUrl = useMemo(() => {
    return `${config.interviewApiBase}/chat`;
  }, []);

  // Pre-emptive request when preamble is completed
  useEffect(() => {
    if (showContinuePrompt && !preemptiveRequest && !isPreemptiveLoading) {
      console.log('[Preemptive Request] Starting pre-emptive interview request');
      setIsPreemptiveLoading(true);
      
      const sendPreemptiveRequest = async () => {
        try {
          const body = buildRequestBody('start');
          const headers = { 'Content-Type': 'application/json' };
          
          const functionsKey = process.env.REACT_APP_INTERVIEW_API_KEY;
          if (functionsKey) {
            headers['x-functions-key'] = functionsKey;
          }
          
          const res = await fetch(voiceUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
          });
          
          const data = await res.json();
          console.log('[Preemptive Request] Request completed successfully');
          setPreemptiveRequest(data);
        } catch (err) {
          console.error('[Preemptive Request] Request failed:', err);
          setPreemptiveRequest({ error: err });
        } finally {
          setIsPreemptiveLoading(false);
        }
      };
      
      sendPreemptiveRequest();
    }
  }, [showContinuePrompt, preemptiveRequest, isPreemptiveLoading, voiceUrl]);

  // Ensure a per-game thread id
  useEffect(() => {
    const generateUUID = () =>
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    try {
      const scope = `${window.location.pathname}`;
      const key = `game_thread:${scope}`;
      let existing = localStorage.getItem(key);
      
      const wasExistingBefore = existing !== null;
      
      if (!existing) {
        existing = generateUUID();
        localStorage.setItem(key, existing);
      }
      
      setThreadId(existing);
      
      if (wasExistingBefore) {
        setPreambleCompleted(true);
        if (Object.keys(preambleAnswers).length === 0) {
          setPreambleAnswers({});
        }
      }
    } catch {
      setThreadId(generateUUID());
    }
  }, []);

  // Load existing interview data if threadId exists (indicates existing interview)
  useEffect(() => {
    const loadExistingInterview = async () => {
      if (!threadId || !interviewId || messages.length > 0) return;
      
      try {
        console.log('[Existing Interview] Loading existing interview data for threadId:', threadId);
        
        // Check if this is an existing interview by looking at localStorage
        const scope = `${window.location.pathname}`;
        const key = `game_thread:${scope}`;
        const wasExistingBefore = localStorage.getItem(key) !== null;
        
        if (!wasExistingBefore) {
          console.log('[Existing Interview] This is a new interview, not loading existing data');
          return;
        }
        
        // Try to fetch existing interview data
        const interviewData = await fetchInterviewBySessionId({ sessionId: threadId });
        
        // Handle the actual response structure: { success: true, interview: {...}, messages: [...] }
        const messages = interviewData?.messages || [];
        
        if (messages && Array.isArray(messages)) {
          console.log('[Existing Interview] Found existing interview with', messages.length, 'messages');
          
          // Convert messages to frontend format and load them
          const existingMessages = messages.map(msg => {
            let content = msg.content;
            
            // Parse JSON content for assistant messages
            if (msg.role === 'assistant' && typeof msg.content === 'string') {
              try {
                // Check if content looks like JSON (starts with {)
                if (msg.content.trim().startsWith('{')) {
                  // Unescape the JSON string (convert \n to actual newlines, etc.)
                  const unescapedContent = msg.content
                    .replace(/\\n/g, '\n')
                    .replace(/\\"/g, '"')
                    .replace(/\\\\/g, '\\');
                  
                  const parsedContent = JSON.parse(unescapedContent);
                  // Extract the actual question from the parsed JSON
                  content = parsedContent.question || msg.content;
                }
              } catch (error) {
                // If parsing fails, keep original content
                console.log('[Existing Interview] Failed to parse JSON content, keeping original:', error);
                console.log('[Existing Interview] Content that failed to parse:', msg.content);
                content = msg.content;
              }
            }
            
            return {
              role: msg.role,
              content: content,
              // Include emotion data if available
              ...(msg.sentimentEmotionMap && { sentimentEmotionMap: msg.sentimentEmotionMap }),
              ...(msg.interviewState && { interviewState: msg.interviewState })
            };
          });
          
          setMessages(existingMessages);
          
          // Update signature state with emotion data from the last assistant message
          const lastAssistantMessage = existingMessages.findLast(msg => msg.role === 'assistant');
          if (lastAssistantMessage?.interviewState) {
            console.log('[Existing Interview] Updating signature state with emotion data:', lastAssistantMessage.interviewState);
            setSignatureState(prev => ({
              ...prev,
              emotional_tone: lastAssistantMessage.interviewState.emotional_tone || prev.emotional_tone,
              engagement_level: lastAssistantMessage.interviewState.engagement_level || prev.engagement_level,
              cluster_progress: lastAssistantMessage.interviewState.cluster_progress || prev.cluster_progress,
              signature_element: lastAssistantMessage.interviewState.signature_element || prev.signature_element,
              visual_intensity: lastAssistantMessage.interviewState.visual_intensity || prev.visual_intensity,
              processed_emotions: lastAssistantMessage.interviewState.processed_emotions || prev.processed_emotions
            }));
            
            // Show signature if we have emotion data
            if (lastAssistantMessage.interviewState.processed_emotions && lastAssistantMessage.interviewState.processed_emotions.length > 0) {
              setShowSignature(true);
            }
          }
          
          console.log('[Existing Interview] Successfully loaded existing interview data');
        } else {
          console.log('[Existing Interview] No existing messages found');
        }
      } catch (error) {
        console.error('[Existing Interview] Error loading existing interview:', error);
        // Continue with new interview if loading fails
      }
    };
    
    loadExistingInterview();
  }, [threadId, interviewId, messages.length]);

  
  // Scroll to bottom
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, isSending]);

  const appendMessage = (role, content) => {
    if (!content) return;
    console.log('[Messages] Appending message:', role, content);
    setMessages(prev => {
      const newMessages = [...prev, { role, content }];
      console.log('[Messages] New messages array:', newMessages);
      return newMessages;
    });
  };

  // When the latest message is from the assistant, mark it as the one being "spoken"
  useEffect(() => {
    if (!messages.length) return;
    const lastIndex = messages.length - 1;
    const last = messages[lastIndex];
    if (last.role === 'assistant') {
      setSpeakingIndex(lastIndex);
    }
  }, [messages]);

  const extractAssistantReply = (respJson) => {
    const currentQuestion = respJson?.current_question;
    if (currentQuestion) {
      return currentQuestion;
    }
    
    const g = respJson?.data?.generateCopilotResponse;
    if (g?.messages && Array.isArray(g.messages)) {
      for (let i = g.messages.length - 1; i >= 0; i--) {
        const m = g.messages[i];
        if (m?.role === 'assistant') {
          if (Array.isArray(m.content)) return m.content.join('\n');
          if (typeof m.content === 'string') return m.content;
        }
      }
    }
    return null;
  };

  const processExtensions = (data) => {
    try {
      // Handle interview state for signature display
      if (data.interviewState) {
        setSignatureState(prev => ({
          ...prev,
          emotional_tone: data.interviewState.emotional_tone || prev.emotional_tone,
          engagement_level: data.interviewState.engagement_level || prev.engagement_level,
          cluster_progress: data.interviewState.cluster_progress || prev.cluster_progress,
          signature_element: data.interviewState.signature_element || prev.signature_element,
          visual_intensity: data.interviewState.visual_intensity || prev.visual_intensity
        }));

        // Update portrait height based on engagement
        if (data.interviewState.engagement_level === 'high') {
          setPortraitHeight(330);
        }

        // Use processed emotions from backend if available, otherwise fall back to sentimentEmotionMap
        if (data.interviewState?.processed_emotions) {
          const emotions = data.interviewState.processed_emotions.map(pe => pe.emotion);
          setSignatureState(prev => ({
            ...prev,
            emotions: emotions,
            processed_emotions: data.interviewState.processed_emotions,
            beads: Math.min(emotions.length + 2, 5)
          }));
        } else if (data.sentimentEmotionMap) {
          // Fallback to original mapping if backend doesn't provide processed emotions
          const emotions = Object.values(data.sentimentEmotionMap)
            .flatMap(map => map.canonicalEmotions || [])
            .map(emotion => emotionMapping[emotion] || emotion)
            .slice(0, 3);
          setSignatureState(prev => ({
            ...prev,
            emotions: emotions,
            beads: Math.min(emotions.length + 2, 5)
          }));
        }
      }

      // Handle direct interview service response format
      if (data.pills && onPills) onPills(data.pills);
      if (data.introduction && onIntroduction) onIntroduction(data.introduction);
      if (data.interview_id && onInterviewId) onInterviewId(data.interview_id);
      if (data.should_terminate && onShouldTerminate) onShouldTerminate(Boolean(data.should_terminate));

      // Fallback to old format for compatibility
      const ext = data?.data?.generateCopilotResponse?.extensions;
      if (ext) {
        if (onPills && 'pills' in ext) onPills(ext.pills);
        if (onIntroduction && 'introduction' in ext) onIntroduction(ext.introduction);
        if (onInterviewId && 'interview_id' in ext) onInterviewId(ext.interview_id);
        if (onShouldTerminate && 'shouldTerminate' in ext)
          onShouldTerminate(Boolean(ext.shouldTerminate));
      }

      const shouldTerminate = data.should_terminate || ext?.shouldTerminate;
      if (shouldTerminate) {
        try {
          const scope = `${window.location.pathname}`;
          const key = `game_thread:${scope}`;
          localStorage.removeItem(key);
        } catch {}
      }

      const userSummary = data.userUnderstandingSummary || ext?.userUnderstandingSummary;
      if (userSummary && shouldTerminate) {
        const sessionId = data.interview_id || ext?.interview_id || threadId || null;
        setValidationModal({
          open: true,
          summary: userSummary,
          summary: userSummary,
          sessionId,
        });
      }

      // Update the current question display (open vs predefined/mixed answer options)
      const isPredefinedLike = data.question_type === 'predefined' || data.question_type === 'mixed' || (data.answers && data.answers.length > 0);
      if (isPredefinedLike) {
        setCurrentQuestion(
          data.question
            ? { question_type: data.question_type || 'predefined', ...data.question }
            : {
                question_type: data.question_type || 'predefined',
                question_text: data.current_question,
                answers: data.answers
              }
        );
      } else {
        setCurrentQuestion(null);
      }
    } catch (err) {
      logger.error('Failed to process extensions:', err);
    }
  };

  const buildRequestBody = (userText, predefinedAnswer = null) => {
    const messagesToSend = messages.map(msg => ({
      role: msg.role,
      content: msg.content
    })).filter(msg => msg.role && msg.content);
    
    messagesToSend.push({
      role: 'user',
      content: userText
    });

    const body = {
      threadId: threadId,
      messages: messagesToSend,
      voiceId: interviewId,
      environment: config.environment,
      frontendUrl: config.frontendUrl
    };
    
    if (Object.keys(preambleAnswers).length > 0 && !showPreambleQuestions && messages.length === 0) {
      body.preambleAnswers = preambleAnswers;
    }

    if (predefinedAnswer) {
      body.predefinedAnswer = predefinedAnswer;
    }
    
    return body;
  };

  const handlePredefinedAnswer = (answer) => {
    onSend(null, answer.label, false, answer);
  };

  const handlePreambleAnswer = (answerIndex) => {
    const currentQuestion = preambleQuestions[currentPreambleIndex];
    const answer = currentQuestion.answers[answerIndex];
    
    const updatedAnswers = {
      ...preambleAnswers,
      [currentQuestion.question_text || currentPreambleIndex]: {
        question: currentQuestion.question_text,
        answer: answer.label,
        anchor: answer.anchor,
        valence: answer.valence,
        answerIndex: answerIndex
      }
    };
    setPreambleAnswers(updatedAnswers);
    
    if (currentPreambleIndex < preambleQuestions.length - 1) {
      setCurrentPreambleIndex(currentPreambleIndex + 1);
      console.log('[Preamble] Moving to next question:', currentPreambleIndex + 1);
    } else {
      console.log('[Preamble] All questions answered, hiding preamble section');
      
      setCurrentPreambleIndex(currentPreambleIndex + 1);
      setShowPreambleQuestions(false);
      
      console.log('[Preamble] State after completion:', {
        showPreambleQuestions: false,
        currentPreambleIndex: currentPreambleIndex + 1,
        preambleAnswersCount: Object.keys(preambleAnswers).length
      });
    }
  };

  const handleContinueToInterview = async () => {
    console.log('[Start Interview] User clicked start button');
    
    // Cancel pre-emptive request if it's running
    if (isPreemptiveLoading) {
      console.log('[Start Interview] Canceling pre-emptive request');
      setIsPreemptiveLoading(false);
      setPreemptiveRequest(null);
    }
    
    // Set states for interview start
    setCanStart(true);
    setShowPreambleQuestions(false);
    setPreambleCompleted(true);
    setShowContinuePrompt(false);
    
    // Send the start message immediately with bypass flag
    setTimeout(() => {
      try {
        onSend({ preventDefault: () => {} }, 'start', true); // Pass bypass flag
      } catch (error) {
        console.error('[Interview Start] Error calling onSend:', error);
      }
    }, 50);
  };

  const onSend = async (e, overrideText, bypassCanStartCheck = false, predefinedAnswer = null) => {
    console.log('[onSend] Called with overrideText:', overrideText, 'bypass:', bypassCanStartCheck);
    console.log('[onSend] Call stack:', new Error().stack);
    e?.preventDefault?.();
    
    if (disabled || (!bypassCanStartCheck && canStart !== true)) {
      console.log('[onSend] Early return - disabled or canStart not true');
      return;
    }
    
    const text = (overrideText !== undefined ? String(overrideText) : input).trim();
    if (!text || isSending) {
      console.log('[onSend] Early return - no text or already sending');
      return;
    }
    
    // Check for pre-emptive request if this is the initial 'start' message
    if (text === 'start' && preemptiveRequest && !preemptiveRequest.error) {
      console.log('[onSend] Using pre-emptive request result');
      setSpeakingIndex(null);
      appendMessage('user', text);
      setIsInitialStart(false);
      const assistant = extractAssistantReply(preemptiveRequest);
      if (assistant) appendMessage('assistant', assistant);
      processExtensions(preemptiveRequest);
      setPreemptiveRequest(null); // Clear the pre-emptive request
      return;
    }
    
    // If this is a 'start' message and pre-emptive is loading, proceed with normal flow
    // (pre-emptive request was canceled in handleContinueToInterview)
    if (text === 'start' && isPreemptiveLoading) {
      console.log('[onSend] Pre-emptive request was canceled, proceeding with normal flow');
      // Don't return here - proceed to normal send flow to show loading
    }
    
    console.log('[onSend] Proceeding with send');
    console.log('[onSend] Current state - isSending:', isSending, 'isPreemptiveLoading:', isPreemptiveLoading);
    setSpeakingIndex(null);
    appendMessage('user', text);
    if (overrideText === undefined) setInput('');
    setIsSending(true);
    console.log('[onSend] Set isSending to true');

    try {
      const body = buildRequestBody(text, predefinedAnswer);
      const headers = { 'Content-Type': 'application/json' };
      
      const functionsKey = process.env.REACT_APP_INTERVIEW_API_KEY;
      if (functionsKey) {
        headers['x-functions-key'] = functionsKey;
      }
      const res = await fetch(voiceUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      console.log('[Response] Received data:', data);
      setIsInitialStart(false);
      const assistant = extractAssistantReply(data);
      console.log('[Response] Extracted assistant:', assistant);
      if (assistant) {
        console.log('[Response] Appending assistant message');
        appendMessage('assistant', assistant);
      } else {
        console.log('[Response] No assistant message found');
      }
      processExtensions(data);
    } catch (err) {
      logger.error('Interview chat send failed:', err);
      appendMessage('assistant', 'Sorry, something went wrong. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const onTextareaKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend(e);
    }
  };

  const AnimatedText = ({ message, onComplete, isHtml = false }) => {
    const [wordIndex, setWordIndex] = useState(0);
    const tokens = useMemo(() => {
      const m = message || '';
      if (isHtml) {
        const htmlElementRegex = /<(strong|em|code|b|i|u|span|div|p|h[1-6]|ul|ol|li|blockquote|a)[^>]*>.*?<\/\1>|<[^>]*>|[^<]+/g;
        const matches = m.match(htmlElementRegex) || [];
        
        const tokens = [];
        matches.forEach(match => {
          if (match.startsWith('<') && match.includes('</')) {
            tokens.push(match);
          } else if (match.startsWith('<')) {
            tokens.push(match);
          } else if (match.endsWith('>')) {
            tokens.push(match);
          } else {
            const textTokens = match.match(/(\S+|\s+)/g) || [];
            tokens.push(...textTokens);
          }
        });
        return tokens;
      } else {
        return m.match(/(\S+|\s+)/g) || [];
      }
    }, [message, isHtml]);
    const hasCompletedRef = useRef(false);

    useEffect(() => {
      if (!tokens.length) return;

      if (wordIndex >= tokens.length) {
        if (!hasCompletedRef.current) {
          hasCompletedRef.current = true;
          if (onComplete) onComplete();
        }
        return;
      }

      const timer = setInterval(() => {
        setWordIndex((prev) => (prev < tokens.length ? prev + 1 : prev));
      }, 60);
      return () => clearInterval(timer);
    }, [wordIndex, tokens.length]);

    const visibleCount = Math.min(wordIndex, tokens.length);

    return (
      <span className={styles.animatedTextWrap} style={{ whiteSpace: 'pre-wrap' }}>
        <span className={styles.animatedTextGhost} aria-hidden="true">
          {message || ''}
        </span>
        <span className={styles.animatedTextVisible}>
          {tokens.slice(0, visibleCount).map((t, i) => {
            if (isHtml && t.startsWith('<')) {
              return <span key={i} dangerouslySetInnerHTML={{ __html: t }} />;
            } else {
              const isWhitespace = /^\s+$/.test(t);
              if (isWhitespace) return <span key={i}>{t}</span>;
              return (
                <span key={i} className={styles.animatedToken}>
                  {t}
                </span>
              );
            }
          })}
        </span>
      </span>
    );
  };

  const SpeakingMessage = ({ content }) => {
    const [isAnimating, setIsAnimating] = useState(true);

    const handleComplete = () => {
      if (!isAnimating) return;
      setIsAnimating(false);
    };

    const contentString = typeof content === 'string' ? content : String(content);
    const hasMarkdown = /\*\*.*?\*\*|\*.*?\*|`.*?`|^#{1,6}\s/m.test(contentString);

    const getProcessedContent = (text) => {
      return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>')
        .replace(/^#{1,6}\s(.*)$/gm, '<strong>$1</strong>');
    };

    const processedContent = hasMarkdown ? getProcessedContent(contentString) : contentString;

    return (
      <div className={styles.speakingMessage}>
        <div className={styles.avatarContainer}>
          {isAnimating ? (
            <BoopTalk />
          ) : (
            <img src={boopStatic} alt="Assistant" className={styles.staticAvatar} />
          )}
        </div>
        <div className={styles.messageContent}>
          <AnimatedText
            message={processedContent}
            onComplete={handleComplete}
            isHtml={hasMarkdown}
          />
        </div>
      </div>
    );
  };

  const LoadingIndicator = ({ message }) => {
    const hasExistingMessages = messages.length > 0;
    
    const defaultTitle = hasExistingMessages ? 'Continuing your interview…' : 'Loading your interview…';
    const defaultBody = hasExistingMessages 
      ? '\n\nPicking up where you left off. Your previous responses are being loaded.' +
        '\n\nTake your time to continue when you\'re ready.'
      : '\n\nThis is a private, anonymous conversation — built uniquely around your responses.' +
        '\nTake your time. Be honest. What you share helps create clarity and action.' +
        '\n\nLet\'s start.';

    try {
      if (BoopAnimate && typeof BoopAnimate === 'function') {
        return (
          <div className={styles.loadingContainer}>
            <div className={styles.avatarContainer}>
              <BoopAnimate />
            </div>
            <div className={styles.loadingContent}>
              {message ? (
                message === '...' ? (
                  <div className={styles.thinkingDots} aria-label="Thinking" aria-live="polite">
                    <span></span><span></span><span></span>
                  </div>
                ) : (
                  <AnimatedText message={message} />
                )
              ) : (
                <>
                  <strong className={styles.loadingTitle}>{defaultTitle}</strong>
                  <AnimatedText message={defaultBody} />
                </>
              )}
            </div>
          </div>
        );
      }
    } catch {}
    return (
      <div className={styles.typingDots} aria-hidden="true">
        <span></span><span></span><span></span>
      </div>
    );
  };

  const renderedMessages = useMemo(() => {
    console.log('[RenderedMessages] Rendering messages:', messages);
    let hiddenStart = false;
    return messages.map((m, idx) => {
      const isStart = typeof m.content === 'string' && m.content.trim().toLowerCase() === 'start';
      
      if (!hiddenStart && m.role === 'user' && isStart) {
        hiddenStart = true;
        console.log('[RenderedMessages] Hiding start message');
        return null;
      }
      const isAssistant = m.role === 'assistant';
      const isSpeaking = !isSending && isAssistant && idx === speakingIndex;
      return (
        <div key={idx} className={`${styles.message} ${m.role === 'user' ? styles.user : styles.assistant}`}>
          <div className={styles.bubble}>
            {isSpeaking ? <SpeakingMessage content={m.content} /> : 
             isAssistant ? (
               <ReactMarkdown 
                 components={{
                   p: ({children}) => <p className={styles.messageParagraph}>{children}</p>,
                   strong: ({children}) => <strong className={styles.messageStrong}>{children}</strong>,
                   em: ({children}) => <em className={styles.messageEm}>{children}</em>,
                   ul: ({children}) => <ul className={styles.messageList}>{children}</ul>,
                   ol: ({children}) => <ol className={styles.messageList}>{children}</ol>,
                   li: ({children}) => <li className={styles.messageListItem}>{children}</li>,
                   h1: ({children}) => <h1 className={styles.messageH1}>{children}</h1>,
                   h2: ({children}) => <h2 className={styles.messageH2}>{children}</h2>,
                   h3: ({children}) => <h3 className={styles.messageH3}>{children}</h3>,
                   blockquote: ({children}) => (
                     <blockquote className={styles.messageBlockquote}>{children}</blockquote>
                   ),
                   code: ({children}) => (
                     <code className={styles.messageCode}>{children}</code>
                   )
                 }}
               >
                 {m.content}
               </ReactMarkdown>
             ) : (
               m.content
             )
            }
          </div>
        </div>
      );
    });
  }, [messages, isSending, speakingIndex]);

  const defaultBody = 
      '\n\nThis is a private, anonymous conversation — built uniquely around your responses.' +
      '\nTake your time. Be honest. What you share helps create clarity and action.' +
      '\n\nLet\'s start.';

  const userReplyCount = useMemo(() => {
    return messages.filter((m) => {
      if (m.role !== 'user') return false;
      const isStart = typeof m.content === 'string' && m.content.trim().toLowerCase() === 'start';
      return !isStart;
    }).length;
  }, [messages]);

  // Communicate interview state to navigation bar
  useEffect(() => {
    const canEndInterview = userReplyCount >= 3 && !disabled && !isSending;
    console.log('[SignatureChat] Emitting interview state:', { canEndInterview, disabled, isSending, userReplyCount });
    window.dispatchEvent(new CustomEvent('interviewStateChanged', { 
      detail: { canEndInterview, disabled, isSending, userReplyCount }
    }));
  }, [userReplyCount, disabled, isSending]);

  // Listen for end interview request from navigation bar
  useEffect(() => {
    const handleEndInterviewRequested = () => {
      setShowEndInterviewModal(true);
    };

    window.addEventListener('endInterviewRequested', handleEndInterviewRequested);
    
    return () => {
      window.removeEventListener('endInterviewRequested', handleEndInterviewRequested);
    };
  }, []);

  // Emit interview page detection event for NavigationBar
  useEffect(() => {
    // Emit event to notify NavigationBar that this is an interview page
    window.dispatchEvent(new CustomEvent('interviewPageDetected', { 
      detail: { isInterviewPage: true } 
    }));
    
    // Cleanup event when component unmounts
    return () => {
      window.dispatchEvent(new CustomEvent('interviewPageDetected', { 
        detail: { isInterviewPage: false } 
      }));
    };
  }, []);

  // Check screen size to determine if NavigationBar should show mobile button
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = window.innerWidth <= 768;
      // On mobile, NavigationBar will show the button, so hide SignatureChat's mobile button
      setShouldShowMobileEndButton(!isMobile);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  if (canStart === false && !showPreambleQuestions && !showContinuePrompt) {
    return (
      <Container>
        <div className={styles.homepage}>
            <h1 className={styles.homepageTitle}>Sorry the interview is not yet available</h1>
        </div>
      </Container>
    );
  }

  // Function to generate signature element shape based on element type and emotional tone
  const getSignatureElementShape = (element, emotionalTone) => {
    // Get color from emotion palette based on emotional tone (not from processed emotions)
    const mappedEmotion = emotionMapping[emotionalTone] || emotionalTone;
    const color = emotionPalette[mappedEmotion]?.color || '#B9B9B2';
    
    // Define shape styles based on element type
    const shapeStyles = {
      // Circle shapes
      circle_soft: {
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        boxShadow: `0 0 20px ${color}40`
      },
      circle_medium: {
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        boxShadow: `0 0 30px ${color}60`
      },
      circle_bright: {
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        boxShadow: `0 0 40px ${color}80`,
        border: `2px solid ${color}`
      },
      
      // Flame shapes
      flame_soft: {
        width: '40px',
        height: '60px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
        boxShadow: `0 0 25px ${color}50`,
        transform: 'scaleY(1.2)'
      },
      flame_medium: {
        width: '50px',
        height: '75px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
        boxShadow: `0 0 35px ${color}70`,
        transform: 'scaleY(1.3)'
      },
      flame_bright: {
        width: '60px',
        height: '90px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
        boxShadow: `0 0 45px ${color}90`,
        transform: 'scaleY(1.4)'
      },
      
      // Star shapes
      star_soft: {
        width: '50px',
        height: '50px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
        boxShadow: `0 0 20px ${color}40`
      },
      star_medium: {
        width: '70px',
        height: '70px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
        boxShadow: `0 0 30px ${color}60`
      },
      star_bright: {
        width: '90px',
        height: '90px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
        boxShadow: `0 0 40px ${color}80`
      },
      
      // Heart shapes
      heart_soft: {
        width: '50px',
        height: '50px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        clipPath: 'polygon(50% 25%, 65% 10%, 80% 10%, 95% 25%, 95% 40%, 80% 55%, 65% 70%, 50% 85%, 35% 70%, 20% 55%, 5% 40%, 5% 25%, 20% 10%, 35% 10%)',
        boxShadow: `0 0 20px ${color}40`
      },
      heart_medium: {
        width: '70px',
        height: '70px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        clipPath: 'polygon(50% 25%, 65% 10%, 80% 10%, 95% 25%, 95% 40%, 80% 55%, 65% 70%, 50% 85%, 35% 70%, 20% 55%, 5% 40%, 5% 25%, 20% 10%, 35% 10%)',
        boxShadow: `0 0 30px ${color}60`
      },
      heart_bright: {
        width: '90px',
        height: '90px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        clipPath: 'polygon(50% 25%, 65% 10%, 80% 10%, 95% 25%, 95% 40%, 80% 55%, 65% 70%, 50% 85%, 35% 70%, 20% 55%, 5% 40%, 5% 25%, 20% 10%, 35% 10%)',
        boxShadow: `0 0 40px ${color}80`
      },
      
      // Light shapes (glowing orbs)
      light_soft: {
        width: '60px',
        height: '60px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity * 0.6,
        borderRadius: '50%',
        boxShadow: `0 0 30px ${color}60, inset 0 0 20px ${color}40`,
        background: `radial-gradient(circle, ${color} 0%, ${color}40 50%, transparent 70%)`
      },
      light_medium: {
        width: '80px',
        height: '80px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity * 0.7,
        borderRadius: '50%',
        boxShadow: `0 0 40px ${color}80, inset 0 0 25px ${color}60`,
        background: `radial-gradient(circle, ${color} 0%, ${color}60 40%, transparent 60%)`
      },
      light_bright: {
        width: '100px',
        height: '100px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity * 0.8,
        borderRadius: '50%',
        boxShadow: `0 0 50px ${color}A0, inset 0 0 30px ${color}80`,
        background: `radial-gradient(circle, ${color} 0%, ${color}80 30%, transparent 50%)`
      },
      
      // Sparkle shapes (star with extra glow)
      sparkle_soft: {
        width: '45px',
        height: '45px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
        boxShadow: `0 0 25px ${color}60, 0 0 15px ${color}40`
      },
      sparkle_medium: {
        width: '65px',
        height: '65px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
        boxShadow: `0 0 35px ${color}80, 0 0 20px ${color}60`
      },
      sparkle_bright: {
        width: '85px',
        height: '85px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
        boxShadow: `0 0 45px ${color}A0, 0 0 25px ${color}80`
      },
      
      // Pulse shapes (pulsing circles)
      pulse_soft: {
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: `3px solid ${color}`,
        opacity: signatureState.visual_intensity * 0.6,
        boxShadow: `0 0 20px ${color}40`
      },
      pulse_medium: {
        width: '70px',
        height: '70px',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: `4px solid ${color}`,
        opacity: signatureState.visual_intensity * 0.7,
        boxShadow: `0 0 30px ${color}60`
      },
      pulse_bright: {
        width: '90px',
        height: '90px',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: `5px solid ${color}`,
        opacity: signatureState.visual_intensity * 0.8,
        boxShadow: `0 0 40px ${color}80`
      },
      
      // Wave shapes (flowing curves)
      wave_soft: {
        width: '60px',
        height: '40px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        borderRadius: '50% 50% 0 50%',
        transform: 'rotate(-45deg)',
        boxShadow: `0 0 20px ${color}40`
      },
      wave_medium: {
        width: '80px',
        height: '50px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        borderRadius: '50% 50% 0 50%',
        transform: 'rotate(-45deg)',
        boxShadow: `0 0 30px ${color}60`
      },
      wave_bright: {
        width: '100px',
        height: '60px',
        backgroundColor: color,
        opacity: signatureState.visual_intensity,
        borderRadius: '50% 50% 0 50%',
        transform: 'rotate(-45deg)',
        boxShadow: `0 0 40px ${color}80`
      }
    };
    
    return (
      <div 
        className={styles.signatureShape}
        style={shapeStyles[element] || shapeStyles.circle_medium}
      />
    );
  };

  return (
    <div className={styles.signatureContainer}>
      {/* Header */}
      <div className={styles.head}>
        <div className={styles.brandmark}>AUGUST × SAYS</div>
        {/* <div className={styles.headline}>The reward for answering: an accurate picture of you, built as you talk</div>
        <div className={styles.caption}>A loyalty activation at the match. Watch your emotional signature take shape — yours to keep. Tap through it.</div> */}
      </div>

      {/* Portrait - The Signature */}
      <div 
        ref={portraitRef}
        className={styles.portrait} 
        style={{ height: `${portraitHeight}px` }}
      >
          <div className={styles.auraContainer}>
            {signatureState.processed_emotions?.map((emotionData, index) => {
              if (!emotionData) return null;
              
              // Use color directly from processed emotions
              const color = emotionData.color;
              
              if (!color) return null;
              
              // Get container dimensions for responsive sizing
              const containerWidth = portraitRef.current?.offsetWidth || 600;
              const containerHeight = portraitHeight || 250;
              const minDimension = Math.min(containerWidth, containerHeight);
              
              // Calculate sizes relative to container (30% to 80% of container size)
              const baseSize = minDimension * 0.4; // 40% of container as base
              const engagementMultiplier = signatureState.engagement_level === 'high' ? 1.5 : 
                                         signatureState.engagement_level === 'medium' ? 1.0 : 0.7;
              
              // Create more varied positioning using different patterns for each emotion
              const angleOffset = (index * 137.5) * (Math.PI / 180); // Golden angle for better distribution
              const radiusVariation = (minDimension * 0.15) + (index * minDimension * 0.05) * engagementMultiplier;
              const xPos = Math.sin(angleOffset) * radiusVariation;
              const yPos = Math.cos(angleOffset) * radiusVariation;
              
              // Add some randomness for organic feel (scaled to container)
              const randomOffset = (Math.sin(index * 7.3) * minDimension * 0.02) + (Math.cos(index * 11.7) * minDimension * 0.02);
              
              // Calculate blob size based on container and engagement
              const blobSize = baseSize * (1 + index * 0.75) * engagementMultiplier;
              
              return (
                <div
                  key={index}
                  className={styles.emotionBlob}
                  style={{
                    backgroundColor: color,
                    opacity: signatureState.visual_intensity,
                    transform: `translate(${xPos + randomOffset}px, ${yPos + randomOffset}px)`,
                    animation: `blobFloat ${3 + index * 0.7}s ease-in-out infinite`,
                    // Size relative to container
                    width: `${blobSize}px`,
                    height: `${blobSize}px`
                  }}
                />
              );
            })}
          </div>
          
          <div className={styles.beads}>
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className={`${styles.bead} ${index < signatureState.beads ? styles.on : ''}`}
              />
            ))}
          </div>
          
          {/* Signature Element - Shape matching emotional tone */}
          {signatureState.signature_element && (
            <div className={styles.signatureElement}>
              {getSignatureElementShape(signatureState.signature_element, signatureState.emotional_tone)}
            </div>
          )}
          
          <div className={styles.pLabel}>
            <div className={styles.k}>Your signature</div>
            <div className={styles.t}>
              {signatureState.engagement_level === 'high' ? 'Signature complete.' :
               signatureState.engagement_level === 'medium' ? 'Your signature is taking shape…' :
               'Forming as you talk…'}
            </div>
          </div>
          
          {/* {showSignature && (
            <div className={`${styles.pTitle} ${styles.show}`}>
              <div className={styles.sigK}>Your loyalty signature</div>
              <div className={styles.sigN}>
                {signatureState.emotional_tone === 'joy' ? 'The Spark' :
                 signatureState.emotional_tone === 'pride' ? 'The True Believer' :
                 signatureState.emotional_tone === 'belonging' ? 'The Ride-or-Die' :
                 signatureState.emotional_tone === 'hope' ? 'The Faithful' :
                 signatureState.emotional_tone === 'frustration' ? 'The Heartbroken Loyalist' :
                 signatureState.emotional_tone === 'nerves' ? 'The Quiet Loyalist' :
                 'Your Signature'}
              </div>
              <div className={styles.sigE}>
                {signatureState.emotions.slice(0, 3).map((emotion, index) => {
                  const mappedEmotion = emotionMapping[emotion] || emotion;
                  return (
                    <span key={index} className={styles.etag}>
                      {emotionPalette[mappedEmotion.toLowerCase()]?.label || emotion}
                    </span>
                  );
                })}
              </div>
            </div>
          )} */}
        </div>

        {/* Stage - Chat Interface */}
        <div className={styles.stage}>
          {canStart === null && (
            <div className={styles.loading}>Loading…</div>
          )}

          {validationModal.open && (
            <div className={styles.validationModalOverlay}>
              <div className={styles.validationModal}>
                <h2 className={styles.validationTitle}>Does this summary look correct?</h2>
                <p className={styles.validationSummary}>
                  {validationModal.summary}
                </p>
                <div className={styles.validationButtons}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="md"
                    disabled={isValidating}
                    onClick={() => handleValidationResponse(false)}
                  >
                    No, not quite
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    disabled={isValidating}
                    onClick={() => handleValidationResponse(true)}
                    className={styles.validationYesButton}
                  >
                    Yes, looks right
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Preamble Questions Display */}
          {(() => {
            console.log('[Preamble Render] Checking render conditions:', {
              showPreambleQuestions, currentPreambleIndex, preambleQuestionsLength: preambleQuestions.length
            });
            return showPreambleQuestions && currentPreambleIndex >= 0 && currentPreambleIndex < preambleQuestions.length;
          })() && (
            <div className={styles.preambleContainer}>
              <div className={styles.preambleHeader}>
                <div className={styles.preambleTitle}>Quick Questions</div>
                <div className={styles.preambleProgress}>
                  Question {currentPreambleIndex + 1} of {preambleQuestions.length}
                </div>
              </div>
              
              <div className={styles.preambleQuestion}>
                {preambleQuestions[currentPreambleIndex].question_text}
              </div>
              
              <div className={styles.preambleAnswers}>
                {preambleQuestions[currentPreambleIndex].answers.map((answer, answerIndex) => (
                  <button
                    key={answerIndex}
                    className={styles.preambleAnswerButton}
                    onClick={() => handlePreambleAnswer(answerIndex)}
                    disabled={isSending}
                  >
                    {answer.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Continue Prompt */}
          {showContinuePrompt && (
            <div className={styles.continuePrompt}>
              <div className={styles.continuePromptContent}>
                <h3>Ready to begin?</h3>
                <p>You've completed the preliminary questions. Let's start your interview!</p>
                <button
                  className={styles.continueButton}
                  onClick={handleContinueToInterview}
                  disabled={isSending}
                >
                  Start Interview
                </button>
              </div>
            </div>
          )}

          {preambleCompleted && !showPreambleQuestions && !showContinueConfirmation && !showContinuePrompt && (
            <>
              <div className={styles.preambleTitleBar}>
                <div className={styles.preambleTitleBarTitle}>
                  {canvassName || 'Welcome'}
                </div>
                <div className={styles.preambleProgressDots}>
                  {preambleQuestions.map((_, index) => (
                    <div
                      key={index}
                      className={`${styles.preambleProgressDot} ${styles.completed}`}
                    />
                  ))}
                </div>
              </div>
              
              <div className={styles.messages} ref={listRef}>
                {introductionData?.introduction_text && (
                  <>
                    <div className={styles.intro}>{introductionData.introduction_text}</div>
                    <div className={styles.privacyReminder}>
                      Privacy Reminder: Please don't share personal information (PII) in your responses. Avoid names, emails, phone numbers, addresses, IDs, or anything that could identify you.
                    </div>
                  </>
                )}
                {renderedMessages}
                {isSending && (
                  <>
                    { isInitialStart && !introductionData?.introduction_text && ( 
                      <>
                        <div className={styles.intro}>{defaultBody}</div>
                        <div className={styles.privacyReminder}>
                          Privacy Reminder: Please don't share personal information (PII) in your responses. Avoid names, emails, phone numbers, addresses, IDs, or anything that could identify you.
                        </div>
                      </>
                    )}
                    <LoadingIndicator />
                  </>
                )}
              </div>

              <div className={styles.inputContainer}>
                {(currentQuestion?.question_type === 'predefined' || currentQuestion?.question_type === 'mixed') && (
                  <div className={styles.preambleAnswers}>
                    {currentQuestion.answers.map((answer, answerIndex) => (
                      <button
                        key={answerIndex}
                        className={styles.preambleAnswerButton}
                        onClick={() => handlePredefinedAnswer(answer)}
                        disabled={disabled || isSending}
                      >
                        {answer.label}
                      </button>
                    ))}
                  </div>
                )}
                {currentQuestion?.question_type !== 'predefined' && (
                  <>
                    <textarea
                      className={styles.input}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={onTextareaKeyDown}
                      placeholder="Share your thoughts..."
                      disabled={disabled || isSending}
                      rows={2}
                    />
                    <button
                      className={styles.sendButton}
                      onClick={(e) => onSend(e)}
                      disabled={disabled || isSending || !input.trim()}
                    >
                      Send
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        
                
        {/* End Interview Modal */}
        {showEndInterviewModal && (
          <div className={styles.welcomeModalOverlay} role="presentation">
            <div className={styles.welcomeModal} role="dialog" aria-modal="true" aria-label="End Interview Confirmation">
              <div className={styles.welcomeModalContent}>
                <div className={styles.welcomeTitle}>End Interview?</div>
                <div className={styles.welcomeBody}>
                  <p>
                    Are you sure you want to end the interview? Your insights are valuable and help us understand real experiences better.
                  </p>
                  <p>
                    If you have more to share, continuing for just a few more questions can provide much richer insights. But you're in control - you can end whenever you feel heard.
                  </p>
                </div>
              </div>
              <div className={styles.welcomeModalFooter}>
                <Button 
                  variant="outline-neutral"
                  onClick={() => setShowEndInterviewModal(false)}
                >
                  Continue Sharing
                </Button>
                <Button 
                  variant="primary"
                  onClick={() => {
                    try {
                      onSend({ preventDefault: () => {} }, 'End the Interview');
                      setShowEndInterviewModal(false);
                    } catch {}
                  }}
                >
                  Yes, End Interview
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
    </div>
  );
};

export default SignatureChat;
